/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { Injectable, computed, effect, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import type { Views } from "@app/@classes/supabase";
import type { CbSelectOption } from "@app/widgets/base/select/select.component";
import { UsersTenantsService } from "../data/users-tenant.service";

const VIEW = "users_in_tenant";
type T = typeof VIEW;

@Injectable({
	providedIn: "root",
})
export class UsersRepositoryService extends UsersTenantsService {
	private readonly users = signal<Views<"users_in_tenant">[]>([]);

	public readonly usersMap = computed<Record<string, Views<"users_in_tenant">>>(
		() => {
			return this.users().reduce(
				(acc, user) => {
					if (user.user_id) {
						acc[user.user_id] = user;
					}
					return acc;
				},
				{} as Record<string, Views<"users_in_tenant">>,
			);
		},
	);

	public readonly usersOptions = computed<CbSelectOption[]>(() => {
		return Object.values(this.usersMap()).map((user) => ({
			value: user.user_id,
			label: user.name ?? "",
		}));
	});

	private currentUserId = computed(() => {
		return this.authStateService.currentUserId();
	});

	public readonly currentUserRole = computed<string | null>(() => {
		const users = this.usersMap();
		const userId = this.authStateService.currentUserId();

		if (userId) {
			const user = users[userId];

			if (user) {
				return user.role;
			}
		}

		return null;
	});

	public readonly isAdmin = computed(() => {
		return (
			this.currentUserRole() === "owner" || this.currentUserRole() === "admin"
		);
	});

	public usersObservable = toObservable(this.usersMap);

	constructor() {
		super();

		this.subscribeToRealtime();

		effect(() => {
			this.refreshUsersMap();
		});
	}

	public subscribeToRealtime() {
		this.supabaseClient
			.channel(this.randomUUID())
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "users",
				},
				async (data) => {
					this.refreshUsersMap();
				},
			)
			.subscribe();
	}

	public refreshUsersMap() {
		const id = this.authStateService.currentTenantId();

		if (!id) {
			this.users.set([]);
			return;
		}

		this.getMany({
			filters: {
				tenant_id: {
					active: true,
					column: "tenant_id",
					operator: "eq",
					value: id,
				},
			},
		}).subscribe((response) => {
			this.users.set(response);
		});
	}
}
